import React, { useRef, useEffect } from 'react';

// Whole popup is draggable by grabbing any background area; clicks on
// buttons/inputs/links pass through untouched so the controls still work.
const DraggablePopup = ({ children, initialPos }) => {
    const posRef = useRef(initialPos);
    const divRef = useRef(null);
    const contentRef = useRef(null);
    const sizeRef = useRef(null);

    // Keep the popup growing/shrinking from its center instead of its top-left
    // corner: whenever the content's width/height changes (e.g. switching to
    // the wider transfer view), shift left/top by half the delta so every
    // side expands equally.
    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            if (sizeRef.current) {
                const dw = width - sizeRef.current.width;
                const dh = height - sizeRef.current.height;
                if (dw !== 0 || dh !== 0) {
                    posRef.current = {
                        x: posRef.current.x - dw / 2,
                        y: posRef.current.y - dh / 2,
                    };
                    if (divRef.current) {
                        divRef.current.style.left = posRef.current.x + 'px';
                        divRef.current.style.top = posRef.current.y + 'px';
                    }
                }
            }
            sizeRef.current = { width, height };
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const onMouseDown = (e) => {
        if (e.target.closest('button, input, a, select, textarea, [role="button"]')) return;

        e.preventDefault();
        const startX = e.clientX - posRef.current.x;
        const startY = e.clientY - posRef.current.y;

        const onMove = (e) => {
            posRef.current = { x: e.clientX - startX, y: e.clientY - startY };
            if (divRef.current) {
                divRef.current.style.left = posRef.current.x + 'px';
                divRef.current.style.top = posRef.current.y + 'px';
            }
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    return (
        <div
            ref={divRef}
            onMouseDown={onMouseDown}
            style={{
                position: 'fixed',
                left: posRef.current.x,
                top: posRef.current.y,
                zIndex: 9999,
                userSelect: 'none',
                cursor: 'grab',
            }}
        >
            {children}
        </div>
    );
};

export default DraggablePopup;
